import type { FC } from 'react';
import React from 'react';
import { useIntl } from 'react-intl-next';

import Tooltip from '@atlaskit/tooltip/Tooltip';
import Button from '@atlaskit/button/standard-button';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import { useShortcutStrings } from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	STAR_BUTTON_SHORTCUT,
	UNIVERSAL_STAR_BUTTON_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';

import { HoverButton, HiddenAction } from '../PresentationalComponents.tsx';
import type { ButtonProps } from '../PageStar/PageStarButton.tsx';
import { StarIcon } from '../StarIcon.tsx';

import { spaceStarI18n } from './SpaceStarI18n.ts';
import type { SpaceStarProps } from './SpaceStar.tsx';
import { SpaceStar } from './SpaceStar.tsx';

/**
 * Icon button that allows users to star or unstar a space.
 *
 * Renders as a star icon. Typically used in lists and tables (e.g. Spaces menu, Space directory).
 */
export const SpaceStarButton: FC<ButtonProps & SpaceStarProps> = ({
	size = 'medium',
	outlineAppearance = 'subtle',
	tooltipPosition = 'bottom',
	shortcut,
	label,
	...props
}) => {
	const intl = useIntl();
	const spacing = size === 'small' ? 'compact' : 'default';
	const buttonComponent = outlineAppearance === 'subtle' ? HoverButton : undefined;
	const tooltipLabel = props.isStarred
		? intl.formatMessage(spaceStarI18n.unstar)
		: intl.formatMessage(spaceStarI18n.star);

	const shortcutStrings = useShortcutStrings({ shortcut });
	const buttonLabel = label ?? intl.formatMessage(spaceStarI18n.star);

	return (
		<SpaceStar {...props}>
			{({ toggle: toggleStar }) => (
				<>
					<Tooltip
						content={tooltipLabel}
						shortcut={shortcutStrings}
						position={tooltipPosition}
						hideTooltipOnClick
						hideTooltipOnMouseDown
					>
						{props.isStarred ? (
							<Button
								id={`star-button-${props.spaceKey}`}
								onClick={toggleStar}
								spacing={spacing}
								appearance="subtle"
								iconBefore={<StarIcon isStarred spacing="spacious" />}
								aria-label={buttonLabel}
								aria-labelledby={`star-button-${props.spaceKey} space-link-${props.spaceKey}`}
								aria-pressed
								testId="star-button-starred"
							/>
						) : (
							<HiddenAction>
								<Button
									id={`star-button-${props.spaceKey}`}
									onClick={toggleStar}
									spacing={spacing}
									appearance="subtle"
									iconBefore={<StarIcon isStarred={false} spacing="spacious" />}
									component={buttonComponent}
									aria-label={buttonLabel}
									aria-labelledby={`star-button-${props.spaceKey} space-link-${props.spaceKey}`}
									aria-pressed={false}
									testId="star-button-unstarred"
								/>
							</HiddenAction>
						)}
					</Tooltip>
					{shortcut && (
						<GeneralShortcutListener
							deprecatedAccelerator={STAR_BUTTON_SHORTCUT}
							accelerator={UNIVERSAL_STAR_BUTTON_SHORTCUT}
							listener={toggleStar}
						/>
					)}
				</>
			)}
		</SpaceStar>
	);
};
